import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogCancel, AlertDialogAction
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { 
  ShoppingCart, 
  Plus, 
  Search, 
  Package,
  Clock,
  CheckCircle,
  Calendar,
  DollarSign,
  User,
  Car,
  Eye,
  FileText,
  Edit2,
  Calculator,
  TrendingUp,
  Trash2,
  ChevronDown,
  Download
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/errorHandler';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

// Helper function to convert image paths to API endpoint
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If it's already a full URL (OCI direct URL), return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Handle vehicle_images format: "/uploads/vehicle_images/{vehicle_id}/{filename}.jpg"
  const vehicleImageMatch = imagePath.match(/\/uploads\/vehicle_images\/([^/]+)\/([^/]+)/);
  if (vehicleImageMatch) {
    return `${API}/images/${vehicleImageMatch[1]}/${vehicleImageMatch[2]}`;
  }
  // Handle additional_images format: "/uploads/additional_images/{vehicle_id}/{filename}.jpg"
  const additionalImageMatch = imagePath.match(/\/uploads\/additional_images\/([^/]+)\/([^/]+)/);
  if (additionalImageMatch) {
    return `${API}/additional-images/${additionalImageMatch[1]}/${additionalImageMatch[2]}`;
  }
  return `${BACKEND_URL}${imagePath}`; // Fallback to old format
};

const OrderManagement = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogStatus, setEmailDialogStatus] = useState('');
  const [emailDialogOrderIds, setEmailDialogOrderIds] = useState([]);
  const [emailDialogBulkMode, setEmailDialogBulkMode] = useState(false);

  // Check if user has permission for an action
  const hasPermission = (action) => {
    // Admin has all permissions
    if (user?.role === 'admin') return true;
    
    // Check employee permissions
    if (user?.role === 'employee' && user?.permissions) {
      return user.permissions[action] === true;
    }
    
    return false;
  };
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isEditMode, setIsEditMode] = useState(false);
  const [sameAsConsignee, setSameAsConsignee] = useState(true); // Checkbox state
  const [vehiclesInOrders, setVehiclesInOrders] = useState(new Set());
  const [formData, setFormData] = useState({
    customer_id: '',
    vehicle_ids: [], // Support multiple vehicles
    vehicle_id: '', // Keep for backward compatibility
    total_amount_jpy: '',
    total_amount_usd: '',
    exchange_rate: '150',
    status: 'purchased',
    order_date: '',
    reservation_valid_date: '',
    notes: '',
    assigned_employee_id: '',
    fob_usd: '',
    freight_usd: '',
    vanning_usd: '',
    inspection_usd: '',
    order_cost_jpy: '', // Total vehicle cost for commission calculation
    consignee_name: '',
    consignee_address: '',
    consignee_phone: '',
    consignee_email: '',
    notifier_name: '',
    notifier_address: '',
    notifier_phone: '',
    notifier_email: '',
    company_registration_no: '',
    vat_no: '',
    passport_number: '',
    reverse_mileage_requested: false,
    display_mileage: '',
    reverse_mileage_fee_usd: 0
  });
  
  // Track selected customer's preferred currency
  const [customerCurrency, setCustomerCurrency] = useState('USD');

  // When customer selection changes, update currency
  useEffect(() => {
    if (formData.customer_id) {
      const selected = customers.find(c => c.id === formData.customer_id);
      setCustomerCurrency(selected?.currency || 'USD');
    } else {
      setCustomerCurrency('USD');
    }
  }, [formData.customer_id, customers]);

  // Cost fields always store USD — no auto-conversion
  const costCurrency = 'USD';
  const displayUsd = (usdValue) => {
    if (usdValue === '' || usdValue === undefined || usdValue === null) return '';
    return String(usdValue);
  };

  // Local-safe date helpers (avoid UTC off-by-one from toISOString slicing)
  const addDays = (dateStr, days) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return '';
    const [y, m, d] = parts;
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + days);
    const yy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  const formatToDateInput = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Per-vehicle cost breakdowns
  const [vehicleCosts, setVehicleCosts] = useState({});
  
  // Per-vehicle selling prices (for multi-vehicle order splitting)
  const [vehiclePrices, setVehiclePrices] = useState({});
  
  // Per-vehicle reverse mileage settings: { vehicleId: { requested: bool, display_mileage: string } }
  const [reverseMileagePerVehicle, setReverseMileagePerVehicle] = useState({});
  
  // Wallet deduction state
  const [useWallet, setUseWallet] = useState(false);
  const [customerWallet, setCustomerWallet] = useState(null);
  
  // Auto-calculate total amounts when vehicle costs or exchange rate change
  useEffect(() => {
    let totalFobUsd = 0;
    let totalFreightUsd = 0;
    let totalVanningUsd = 0;
    let totalInspectionUsd = 0;
    
    formData.vehicle_ids.forEach(vehicleId => {
      const costs = vehicleCosts[vehicleId] || {};
      totalFobUsd += parseFloat(costs.fob_usd) || 0;
      totalFreightUsd += parseFloat(costs.freight_usd) || 0;
      totalVanningUsd += parseFloat(costs.vanning_usd) || 0;
      totalInspectionUsd += parseFloat(costs.inspection_usd) || 0;
    });
    
    const reverseMileageCount = formData.vehicle_ids.filter(id => reverseMileagePerVehicle[id]?.requested).length;
    const reverseMileageFee = reverseMileageCount * 100;
    const totalAmountUsd = totalFobUsd + totalFreightUsd + totalVanningUsd + totalInspectionUsd + reverseMileageFee;
    const exchangeRate = parseFloat(formData.exchange_rate) || 150;
    const totalAmountJpy = totalAmountUsd * exchangeRate;
    
    setFormData(prev => ({
      ...prev,
      total_amount_usd: totalAmountUsd.toFixed(2),
      total_amount_jpy: totalAmountJpy.toFixed(0),
      fob_usd: totalFobUsd.toFixed(2),
      freight_usd: totalFreightUsd.toFixed(2),
      vanning_usd: totalVanningUsd.toFixed(2),
      inspection_usd: totalInspectionUsd.toFixed(2),
      reverse_mileage_fee_usd: reverseMileageFee
    }));
  }, [vehicleCosts, formData.vehicle_ids, formData.exchange_rate, reverseMileagePerVehicle]);

  // Function to download images as a zip file
  const downloadImages = async (images, folderName) => {
    try {
      // Dynamically import JSZip to create a zip file
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      toast.info(`Preparing ${images.length} images for download...`);
      
      // Download each image and add to zip
      const downloadPromises = images.map(async (img, idx) => {
        const imageUrl = getImageUrl(img);
        try {
          const response = await fetch(imageUrl);
          if (response.ok) {
            const blob = await response.blob();
            const filename = img.split('/').pop() || `image_${idx + 1}.jpg`;
            zip.file(filename, blob);
            return true;
          } else {
            console.warn(`Failed to download image: ${imageUrl}`);
            return false;
          }
        } catch (error) {
          console.error(`Error downloading image ${imageUrl}:`, error);
          return false;
        }
      });

      const results = await Promise.all(downloadPromises);
      const successCount = results.filter(Boolean).length;
      
      if (successCount === 0) {
        toast.error('No images could be downloaded');
        return;
      }

      // Generate and download the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename based on order and folder type
      const orderNumber = selectedOrder?.id?.slice(-8)?.toUpperCase() || 'ORDER';
      link.download = `${orderNumber}_${folderName}.zip`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${successCount}/${images.length} images successfully`);
    } catch (error) {
      console.error('Error creating zip file:', error);
      toast.error('Failed to create zip file');
    }
  };

  useEffect(() => {
    // Only fetch orders if user has permission
    if (user?.role === 'customer' || hasPermission('orders_view')) {
      fetchOrders();
    } else {
      setLoading(false);
    }
    
    if (user?.role !== 'customer') {
      // Only fetch vehicles and customers if creating orders is possible
      if (hasPermission('orders_edit')) {
        fetchVehicles();
        fetchCustomers();
      }
    }
  }, []);

  // Refresh vehicles when opening create/edit dialog to get latest cost data
  useEffect(() => {
    if (showCreateDialog || (selectedOrder && isEditMode)) {
      fetchVehicles();
      fetchEmployees();
      // Fetch all orders to know which vehicles are already assigned
      const fetchOrderedVehicles = async () => {
        try {
          const res = await axios.get(`${API}/orders`);
          const allOrders = res.data || [];
          const orderedIds = new Set();
          const currentOrderId = isEditMode ? selectedOrder?.id : null;
          allOrders.forEach(o => {
            if (o.id !== currentOrderId) {
              (o.vehicle_ids || []).forEach(vid => orderedIds.add(vid));
              if (o.vehicle_id && (!o.vehicle_ids || o.vehicle_ids.length === 0)) orderedIds.add(o.vehicle_id);
            }
          });
          setVehiclesInOrders(orderedIds);
        } catch (e) {
          console.error('Failed to fetch existing orders for vehicle check:', e);
        }
      };
      fetchOrderedVehicles();
    }
  }, [showCreateDialog, selectedOrder, isEditMode]);

  // Auto-calculate Total Amount USD and JPY when cost fields or exchange rate change
  useEffect(() => {
    const fob = parseFloat(formData.fob_usd) || 0;
    const freight = parseFloat(formData.freight_usd) || 0;
    const vanning = parseFloat(formData.vanning_usd) || 0;
    const inspection = parseFloat(formData.inspection_usd) || 0;
    const reverseMileageCount = formData.vehicle_ids.filter(id => reverseMileagePerVehicle[id]?.requested).length;
    const reverseMileageFee = reverseMileageCount * 100;
    const exchangeRate = parseFloat(formData.exchange_rate) || 150;

    const totalUSD = fob + freight + vanning + inspection + reverseMileageFee;
    const totalJPY = totalUSD * exchangeRate;

    setFormData(prev => ({
      ...prev,
      total_amount_usd: totalUSD.toString(),
      total_amount_jpy: totalJPY.toString(),
      reverse_mileage_fee_usd: reverseMileageFee
    }));
  }, [formData.fob_usd, formData.freight_usd, formData.vanning_usd, formData.inspection_usd, formData.exchange_rate, formData.vehicle_ids, reverseMileagePerVehicle]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data || []); // Ensure it's always an array
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
      // Only show error toast for actual errors (not permission issues or empty results)
      if (error.response) {
        // Server responded with error status
        if (error.response.status !== 403 && error.response.status !== 404) {
          toast.error('Failed to load orders');
        }
      } else if (error.request) {
        // Request made but no response (network error)
        toast.error('Network error - Failed to connect');
      }
      // If it's just an empty result or 403/404, don't show error
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?limit=1000`); // Get all vehicles for order creation
      // Handle both paginated and non-paginated response
      if (response.data.vehicles) {
        setVehicles(response.data.vehicles);
      } else if (Array.isArray(response.data)) {
        setVehicles(response.data);
      } else {
        setVehicles([]);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]); // Ensure vehicles is always an array
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
      console.log('Customers fetched:', response.data.length);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers list');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
      console.log('Employees fetched:', response.data.length);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees list');
    }
  };

  const handleVehicleSelect = (vehicleId) => {
    // Vehicle status is NOT restricted — any vehicle can be added to an order
    const selectedVehicle = vehicles.find(v => v.id === vehicleId);
    // Prevent adding vehicles already in another order
    if (vehiclesInOrders.has(vehicleId)) {
      toast.error('This vehicle is already assigned to another order');
      return;
    }
    // Add vehicle to selection if not already selected
    if (!formData.vehicle_ids.includes(vehicleId)) {
      const updatedVehicleIds = [...formData.vehicle_ids, vehicleId];
      
      // Get the selected vehicle's exchange rate
      const vehicleExchangeRate = selectedVehicle?.exchange_rate || formData.exchange_rate || '145';
      
      setFormData(prev => ({
        ...prev,
        vehicle_ids: updatedVehicleIds,
        // Auto-populate exchange rate from first selected vehicle if not manually set
        exchange_rate: prev.vehicle_ids.length === 0 ? vehicleExchangeRate.toString() : prev.exchange_rate
      }));
      
      // Initialize cost breakdown for this vehicle
      setVehicleCosts(prev => ({
        ...prev,
        [vehicleId]: {
          fob_usd: '',
          freight_usd: '',
          vanning_usd: '',
          inspection_usd: ''
        }
      }));
    }
  };

  const handleRemoveVehicle = (vehicleId) => {
    setFormData(prev => ({
      ...prev,
      vehicle_ids: prev.vehicle_ids.filter(id => id !== vehicleId)
    }));
    
    // Remove cost breakdown for this vehicle
    setVehicleCosts(prev => {
      const newCosts = { ...prev };
      delete newCosts[vehicleId];
      return newCosts;
    });
  };
  
  // Handle per-vehicle cost changes
  const handleVehicleCostChange = (vehicleId, field, value) => {
    setVehicleCosts(prev => ({
      ...prev,
      [vehicleId]: {
        ...prev[vehicleId],
        [field]: value
      }
    }));
  };

  // Calculate total costs from selected vehicles for profit calculator
  const calculateSelectedVehiclesCosts = () => {
    // Safety check: ensure vehicles is an array
    if (!Array.isArray(vehicles)) {
      return {
        totalVehicleCostJpy: 0,
        totalVehicleCostUsd: 0,
        exchangeRate: 150,
        selectedVehicles: []
      };
    }
    
    const selectedVehicles = vehicles.filter(v => formData.vehicle_ids.includes(v.id));
    
    // Calculate total using the existing calculateVehicleTotalCost function
    const totalVehicleCostJpy = selectedVehicles.reduce((sum, v) => sum + calculateVehicleTotalCost(v), 0);
    
    // Use vehicle's saved exchange rate for USD conversion
    const vehicleRate = selectedVehicles.length > 0 
      ? getVehicleExchangeRate(selectedVehicles[0]) 
      : 145;
    const exchangeRate = parseFloat(formData.exchange_rate) || vehicleRate;
    const totalVehicleCostUsd = totalVehicleCostJpy / exchangeRate;
    
    return {
      totalVehicleCostJpy,
      totalVehicleCostUsd,
      exchangeRate,
      selectedVehicles
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate USD when JPY changes (CFR)
      if (name === 'total_amount_jpy' && updated.exchange_rate) {
        const jpy = parseFloat(value) || 0;
        const rate = parseFloat(updated.exchange_rate) || 1;
        updated.total_amount_usd = (jpy / rate).toFixed(2);
      }
      
      // Auto-calculate JPY when USD changes (CFR)
      if (name === 'total_amount_usd' && updated.exchange_rate) {
        const usd = parseFloat(value) || 0;
        const rate = parseFloat(updated.exchange_rate) || 1;
        updated.total_amount_jpy = (usd * rate).toFixed(0);
      }
      
      // Sync notifier fields with consignee if checkbox is checked
      if (sameAsConsignee && name.startsWith('consignee_')) {
        const notifierField = name.replace('consignee_', 'notifier_');
        updated[notifierField] = value;
      }
      
      return updated;
    });
  };

  const handleSameAsConsigneeChange = (checked) => {
    setSameAsConsignee(checked);
    
    if (checked) {
      // Copy consignee data to notifier fields
      setFormData(prev => ({
        ...prev,
        notifier_name: prev.consignee_name,
        notifier_address: prev.consignee_address,
        notifier_phone: prev.consignee_phone,
        notifier_email: prev.consignee_email
      }));
    }
  };

  const handleNotifierChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const calculateVehicleTotalCost = (vehicle) => {
    if (!vehicle) return 0;
    // Calculate total vehicle cost from Cost Calculator fields
    // This matches exactly with CostCalculatorTab.calculateTotalCostJPY()
    // Note: inland_cost_jpy already includes domestic_shipping_total
    const purchaseCost = Math.round(vehicle.purchase_cost_jpy || 0);
    const inlandCost = Math.round(vehicle.inland_cost_jpy || 0);  // Already includes domestic_shipping_total
    const vanningCost = Math.round(vehicle.vanning_cost_jpy || 0);
    const freightCost = Math.round(vehicle.freight_per_car_jpy || 0);
    const othersCost = Math.round(vehicle.others_total || 0);
    
    return purchaseCost + inlandCost + vanningCost + freightCost + othersCost;
  };

  // Get the vehicle's saved exchange rate for consistent profit calculations
  const getVehicleExchangeRate = (vehicle) => {
    if (!vehicle) return 150;
    return vehicle.exchange_rate || 145;
  };

  const calculateProfit = () => {
    // Calculate profit based on selected vehicles
    if (!formData.vehicle_ids || formData.vehicle_ids.length === 0) {
      return { profitJPY: 0, profitUSD: 0 };
    }
    
    const { totalVehicleCostJpy, selectedVehicles } = calculateSelectedVehiclesCosts();
    const cfrJPY = parseFloat(formData.total_amount_jpy) || 0;
    
    // Use vehicle's saved exchange rate for USD conversion (for profit display)
    // If multiple vehicles, use average or first vehicle's rate
    const vehicleRate = selectedVehicles.length > 0 
      ? getVehicleExchangeRate(selectedVehicles[0]) 
      : 145;
    const rate = parseFloat(formData.exchange_rate) || vehicleRate;
    
    const profitJPY = cfrJPY - totalVehicleCostJpy;
    const profitUSD = profitJPY / rate;
    
    return { profitJPY, profitUSD };
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setIsEditMode(true);
    
    // Handle both old single-vehicle and new multi-vehicle orders
    let vehicleIds = [];
    if (order.vehicle_ids && Array.isArray(order.vehicle_ids) && order.vehicle_ids.length > 0) {
      // New format: multiple vehicles
      vehicleIds = order.vehicle_ids;
    } else if (order.vehicle_id) {
      // Old format: single vehicle
      vehicleIds = [order.vehicle_id];
    }
    
    // Load existing vehicle costs if available
    if (order.vehicle_costs && typeof order.vehicle_costs === 'object') {
      // Convert all cost values to strings for form inputs
      const loadedCosts = {};
      Object.keys(order.vehicle_costs).forEach(vehicleId => {
        const costs = order.vehicle_costs[vehicleId];
        loadedCosts[vehicleId] = {
          fob_usd: costs.fob_usd?.toString() || '',
          freight_usd: costs.freight_usd?.toString() || '',
          vanning_usd: costs.vanning_usd?.toString() || '',
          inspection_usd: costs.inspection_usd?.toString() || ''
        };
      });
      setVehicleCosts(loadedCosts);
    } else {
      // If no per-vehicle costs, distribute legacy costs equally
      const costPerVehicle = {
        fob_usd: (parseFloat(order.fob_usd) / vehicleIds.length || 0).toString(),
        freight_usd: (parseFloat(order.freight_usd) / vehicleIds.length || 0).toString(),
        vanning_usd: (parseFloat(order.vanning_usd) / vehicleIds.length || 0).toString(),
        inspection_usd: (parseFloat(order.inspection_usd) / vehicleIds.length || 0).toString()
      };
      const initialCosts = {};
      vehicleIds.forEach(vId => {
        initialCosts[vId] = { ...costPerVehicle };
      });
      setVehicleCosts(initialCosts);
    }
    
    // Initialize per-vehicle reverse mileage from order vehicles
    const initialReverseMileage = {};
    if (order.vehicles && Array.isArray(order.vehicles)) {
      order.vehicles.forEach(v => {
        if (v.id && v.reverse_mileage_requested) {
          initialReverseMileage[v.id] = {
            requested: true,
            display_mileage: v.display_mileage?.toString() || ''
          };
        }
      });
    } else {
      // Fallback: check vehicle_ids against the vehicles list
      vehicleIds.forEach(vId => {
        const v = Array.isArray(vehicles) ? vehicles.find(veh => veh.id === vId) : null;
        if (v?.reverse_mileage_requested) {
          initialReverseMileage[vId] = {
            requested: true,
            display_mileage: v.display_mileage?.toString() || ''
          };
        }
      });
    }
    setReverseMileagePerVehicle(initialReverseMileage);
    
    // Find the vehicle for this order (for backward compatibility)
    const vehicle = Array.isArray(vehicles) ? vehicles.find(v => v.id === order.vehicle_id) : null;
    setSelectedVehicle(vehicle);
    
    setFormData({
      customer_id: order.customer_id,
      vehicle_ids: vehicleIds, // Initialize vehicle_ids array properly
      vehicle_id: order.vehicle_id || (vehicleIds.length > 0 ? vehicleIds[0] : ''),
      total_amount_jpy: order.total_amount_jpy?.toString() || '',
      total_amount_usd: order.total_amount_usd?.toString() || '',
      exchange_rate: order.exchange_rate?.toString() || '150',
      status: order.status,
      order_date: order.order_date ? formatToDateInput(order.order_date) : '',
      reservation_valid_date: order.reservation_valid_date
        ? formatToDateInput(order.reservation_valid_date)
        : (order.order_date ? addDays(formatToDateInput(order.order_date), 3) : ''),
      notes: order.notes || '',
      assigned_employee_id: order.assigned_employee_id || '',
      fob_usd: order.fob_usd?.toString() || '',
      freight_usd: order.freight_usd?.toString() || '',
      vanning_usd: order.vanning_usd?.toString() || '',
      inspection_usd: order.inspection_usd?.toString() || '',
      order_cost_jpy: order.order_cost_jpy?.toString() || '',
      consignee_name: order.consignee_name || '',
      consignee_address: order.consignee_address || '',
      consignee_phone: order.consignee_phone || '',
      consignee_email: order.consignee_email || '',
      notifier_name: order.notifier_name || '',
      notifier_address: order.notifier_address || '',
      notifier_phone: order.notifier_phone || '',
      notifier_email: order.notifier_email || '',
      company_registration_no: order.company_registration_no || '',
      vat_no: order.vat_no || '',
      passport_number: order.passport_number || '',
      reverse_mileage_requested: false,
      display_mileage: '',
      reverse_mileage_fee_usd: order.reverse_mileage_fee_usd || 0
    });
    
    // Check if notifier info matches consignee (determine checkbox state)
    const isSameAsConsignee = (
      order.notifier_name === order.consignee_name &&
      order.notifier_address === order.consignee_address &&
      order.notifier_phone === order.consignee_phone &&
      order.notifier_email === order.consignee_email
    );
    setSameAsConsignee(isSameAsConsignee);
    
    setShowEditDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate vehicle selection
    if (!formData.vehicle_ids || formData.vehicle_ids.length === 0) {
      toast.error('Please select at least one vehicle');
      return;
    }
    
    // Prevent submitting vehicles already assigned to other orders
    const conflicted = formData.vehicle_ids.filter(vid => {
      if (isEditMode && selectedOrder) {
        const currentVehicleIds = [...(selectedOrder.vehicle_ids || [])];
        if (selectedOrder.vehicle_id && !currentVehicleIds.includes(selectedOrder.vehicle_id)) {
          currentVehicleIds.push(selectedOrder.vehicle_id);
        }
        if (currentVehicleIds.includes(vid)) return false;
      }
      return vehiclesInOrders.has(vid);
    });
    if (conflicted.length > 0) {
      toast.error('Some vehicles are already assigned to another order. Remove them and try again.');
      return;
    }
    
    try {
      // Convert vehicle costs to numbers and calculate totals
      const parsedVehicleCosts = {};
      let totalFobUsd = 0;
      let totalFreightUsd = 0;
      let totalVanningUsd = 0;
      let totalInspectionUsd = 0;
      
      formData.vehicle_ids.forEach(vehicleId => {
        const costs = vehicleCosts[vehicleId] || {};
        
        const fob = parseFloat(costs.fob_usd) || 0;
        const freight = parseFloat(costs.freight_usd) || 0;
        const vanning = parseFloat(costs.vanning_usd) || 0;
        const inspection = parseFloat(costs.inspection_usd) || 0;
        
        parsedVehicleCosts[vehicleId] = {
          fob_usd: fob,
          freight_usd: freight,
          vanning_usd: vanning,
          inspection_usd: inspection
        };
        
        totalFobUsd += fob;
        totalFreightUsd += freight;
        totalVanningUsd += vanning;
        totalInspectionUsd += inspection;
      });
      
      // Calculate total amount in USD (including reverse mileage fee)
      const reverseMileageCount = formData.vehicle_ids.filter(id => reverseMileagePerVehicle[id]?.requested).length;
      const reverseMileageFee = reverseMileageCount * 100;
      const exchangeRate = parseFloat(formData.exchange_rate) || 150;
      const totalAmountUsd = totalFobUsd + totalFreightUsd + totalVanningUsd + totalInspectionUsd + reverseMileageFee;
      const totalAmountJpy = totalAmountUsd * exchangeRate;
      
      const orderData = {
        ...formData,
        vehicle_ids: formData.vehicle_ids,
        vehicle_costs: parsedVehicleCosts,
        vehicle_prices: Object.keys(vehiclePrices).length > 0 ? vehiclePrices : undefined,
        fob_usd: totalFobUsd,
        freight_usd: totalFreightUsd,
        vanning_usd: totalVanningUsd,
        inspection_usd: totalInspectionUsd,
        total_amount_jpy: totalAmountJpy,
        total_amount_usd: totalAmountUsd,
        exchange_rate: exchangeRate,
        order_cost_jpy: calculateSelectedVehiclesCosts().totalVehicleCostJpy,
        reverse_mileage_fee_usd: reverseMileageFee,
        use_wallet: useWallet
      };
      if (formData.order_date) {
        orderData.order_date = new Date(formData.order_date).toISOString();
      } else {
        delete orderData.order_date;
      }
      if (formData.reservation_valid_date) {
        orderData.reservation_valid_date = new Date(formData.reservation_valid_date).toISOString();
      } else {
        delete orderData.reservation_valid_date;
      }

      // Update vehicles with per-vehicle reverse mileage data
      for (const vehicleId of formData.vehicle_ids) {
        const perVehicle = reverseMileagePerVehicle[vehicleId];
        const mileageValue = perVehicle?.requested ? (parseInt(perVehicle.display_mileage) || null) : null;
        await axios.put(`${API}/vehicles/${vehicleId}`, {
          reverse_mileage_requested: !!perVehicle?.requested,
          display_mileage: mileageValue
        });
      }

      if (isEditMode && selectedOrder) {
        await axios.put(`${API}/orders/${selectedOrder.id}`, orderData);
        toast.success('Order updated successfully!');
        setShowEditDialog(false);
        setIsEditMode(false);
        fetchOrders();
        fetchVehicles();
      } else {
        const newOrder = await axios.post(`${API}/orders`, orderData);
        toast.success('Order created successfully!');
        
        // Deduct from wallet if requested
        if (useWallet && customerWallet) {
          try {
            await axios.post(`${API}/wallets/${formData.customer_id}/deduct`, null, {
              params: {
                amount_usd: totalAmountUsd,
                amount_jpy: 0,
                description: `Order #${newOrder.data.id || ''} - ${formData.vehicle_ids.length} vehicle(s)`
              }
            });
            toast.success(`Wallet deducted $${totalAmountUsd.toLocaleString(undefined, {minimumFractionDigits: 2})}`);
          } catch (walletErr) {
            toast.warning('Order created but wallet deduction failed: ' + (walletErr.response?.data?.detail || 'Unknown error'));
          }
        }
        
        setShowCreateDialog(false);
        fetchOrders();
        fetchVehicles();
      }
      
      setFormData({
        customer_id: '',
        vehicle_ids: [],
        vehicle_id: '',
        total_amount_jpy: '',
        total_amount_usd: '',
        exchange_rate: '150',
        status: 'purchased',
        order_date: '',
        reservation_valid_date: '',
        notes: '',
        assigned_employee_id: '',
        fob_usd: '',
        freight_usd: '',
        vanning_usd: '',
        inspection_usd: '',
        order_cost_jpy: '',
        consignee_name: '',
        consignee_address: '',
        consignee_phone: '',
        consignee_email: '',
        notifier_name: '',
        notifier_address: '',
        notifier_phone: '',
        notifier_email: '',
        company_registration_no: '',
        vat_no: '',
        passport_number: '',
        reverse_mileage_requested: false,
        display_mileage: '',
        reverse_mileage_fee_usd: 0
      });
      setVehicleCosts({}); // Reset vehicle costs
      setVehiclePrices({}); // Reset vehicle prices
      setReverseMileagePerVehicle({}); // Reset per-vehicle reverse mileage
      setSameAsConsignee(true); // Reset checkbox to checked
      setSelectedVehicle(null);
      setSelectedOrder(null);
      setUseWallet(false);
      setCustomerWallet(null);
      fetchOrders();
    } catch (error) {
      console.error('Error saving order:', error);
      const message = extractErrorMessage(error, 'Failed to save order');
      toast.error(message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.length === 0) {
      toast.error('No orders selected');
      return;
    }
    
    const count = selectedOrders.length;
    if (!window.confirm(`Are you sure you want to delete ${count} order(s)? This action cannot be undone.`)) {
      return;
    }
    
    try {
      // Delete all selected orders
      await Promise.all(
        selectedOrders.map(orderId => axios.delete(`${API}/orders/${orderId}`))
      );
      toast.success(`${count} order(s) deleted successfully!`);
      setSelectedOrders([]); // Clear selection
      fetchOrders();
    } catch (error) {
      console.error('Error deleting orders:', error);
      const message = extractErrorMessage(error, 'Failed to delete orders');
      toast.error(message);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedOrders.length === 0 || !newStatus) return;
    setEmailDialogOrderIds([...selectedOrders]);
    setEmailDialogStatus(newStatus);
    setEmailDialogBulkMode(true);
    setEmailDialogOpen(true);
  };
  
  const toggleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };
  
  const toggleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(order => order.id));
    }
  };

  const handleGenerateInvoice = async (orderId, invoiceType = 'PROFORMA') => {
    try {
      const response = await axios.post(`${API}/orders/${orderId}/generate-invoice?invoice_type=${invoiceType}`);
      toast.success(`${invoiceType} Invoice ${response.data.invoice_number} generated successfully!`);
      
      // Download the invoice as blob to avoid save dialog
      const invoiceResponse = await axios.get(`${API}/orders/${orderId}/invoice-download`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([invoiceResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${response.data.invoice_number}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      fetchOrders();
    } catch (error) {
      console.error('Error generating invoice:', error);
      const message = extractErrorMessage(error, `Failed to generate ${invoiceType} invoice`);
      toast.error(message);
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      // Download as blob to avoid save dialog
      const response = await axios.get(`${API}/orders/${orderId}/invoice-download`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${orderId.slice(-8)}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };


  const updateOrderStatus = async (orderId, newStatus) => {
    setEmailDialogOrderIds([orderId]);
    setEmailDialogStatus(newStatus);
    setEmailDialogBulkMode(false);
    setEmailDialogOpen(true);
  };

  const executeStatusUpdate = async (orderIds, status, sendEmail) => {
    try {
      if (orderIds.length === 1) {
        await axios.put(`${API}/orders/${orderIds[0]}/status?status=${status}&send_email=${sendEmail}`);
      } else {
        await axios.put(`${API}/orders/bulk-status`, {
          order_ids: orderIds,
          status: status,
          send_email: sendEmail
        });
      }
      const count = orderIds.length;
      toast.success(count === 1
        ? `Order status updated${sendEmail ? ' — customer notified' : ''}`
        : `${count} order(s) status updated${sendEmail ? ' — customers notified' : ''}`);
      setOrders(prev => prev.map(o => orderIds.includes(o.id) ? { ...o, status } : o));
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-cyan-100 text-cyan-800';
      case 'reserved':
        return 'bg-purple-100 text-purple-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'purchased':
        return 'bg-yellow-100 text-yellow-800';
      case 'sold':
        return 'bg-emerald-100 text-emerald-800';
      case 'booking_applied':
        return 'bg-indigo-100 text-indigo-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'on_the_way':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <Package className="h-4 w-4" />;
      case 'reserved':
        return <Clock className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'purchased':
        return <Package className="h-4 w-4" />;
      case 'sold':
        return <CheckCircle className="h-4 w-4" />;
      case 'booking_applied':
        return <Clock className="h-4 w-4" />;
      case 'shipped':
        return <Clock className="h-4 w-4" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4" />;
      case 'on_the_way':
        return <Clock className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.year} ${vehicle.model} (${vehicle.stock_no})` : 'Unknown Vehicle';
  };

  // Get vehicle count display for multi-vehicle orders
  const getVehicleCountDisplay = (order) => {
    // Check if order has multiple vehicles
    if (order.vehicle_ids && Array.isArray(order.vehicle_ids) && order.vehicle_ids.length > 1) {
      return `${order.vehicle_ids.length} Vehicles`;
    } else if (order.vehicle_ids && Array.isArray(order.vehicle_ids) && order.vehicle_ids.length === 1) {
      // Single vehicle from new format
      return getVehicleInfo(order.vehicle_ids[0]);
    } else if (order.vehicle_id) {
      // Old format: single vehicle
      return getVehicleInfo(order.vehicle_id);
    }
    return 'No Vehicle';
  };

  const getCustomerInfo = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.full_name : 'Unknown Customer';
  };

  const viewOrderDetails = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      const orderData = response.data;
      
      // Fetch vehicle data for all vehicles in the order
      const vehicleIds = [];
      if (orderData.vehicle_ids && Array.isArray(orderData.vehicle_ids) && orderData.vehicle_ids.length > 0) {
        // New format: use vehicle_ids array
        vehicleIds.push(...orderData.vehicle_ids);
      } else if (orderData.vehicle_id) {
        // Old format: single vehicle_id field
        vehicleIds.push(orderData.vehicle_id);
      }
      
      if (vehicleIds.length > 0) {
        try {
          // Fetch all vehicles for this order
          const vehiclePromises = vehicleIds.map(id => axios.get(`${API}/vehicles/${id}`));
          const vehicleResponses = await Promise.all(vehiclePromises);
          orderData.vehicles = vehicleResponses.map(res => res.data);
          
          // Keep backward compatibility - set first vehicle as 'vehicle' field
          if (orderData.vehicles.length > 0) {
            orderData.vehicle = orderData.vehicles[0];
          }
        } catch (vehicleError) {
          console.warn('Could not fetch vehicle data:', vehicleError);
          orderData.vehicles = [];
        }
      }
      
      setSelectedOrder(orderData);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to load order details');
    }
  };

  const filteredOrders = orders.filter(order => {
    // Find customer for this order
    const customer = customers.find(c => c.id === order.customer_id);
    
    // Search by customer name
    let matchesSearch = searchTerm === '' || 
      (customer?.full_name && customer.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Search across all vehicles in the order (multi-vehicle support)
    if (!matchesSearch && order.vehicles && Array.isArray(order.vehicles)) {
      matchesSearch = order.vehicles.some(v => 
        (v.chassis_no && v.chassis_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (v.stock_no && v.stock_no.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Legacy fallback: search single vehicle from vehicles state
    if (!matchesSearch && order.vehicle_id) {
      const vehicle = vehicles.find(v => v.id === order.vehicle_id);
      matchesSearch = (vehicle?.chassis_no && vehicle.chassis_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (vehicle?.stock_no && vehicle.stock_no.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    
    const matchesStatus = !statusFilter || statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-64 mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-96"></div>
        </div>
      </div>
    );
  }

  // Show permission message if user doesn't have access
  if (user?.role !== 'customer' && !hasPermission('orders_view')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="text-center">
          <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Restricted</h2>
          <p className="text-slate-600 max-w-md">
            You don't have permission to view orders. Please contact your administrator to request access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="order-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {user?.role === 'customer' ? 'My Orders' : 'Order Management'}
          </h1>
          <p className="text-slate-600">
            {user?.role === 'customer' 
              ? 'Track your vehicle orders and delivery status'
              : 'Create and manage customer orders'
            }
          </p>
        </div>
        {user?.role !== 'customer' && hasPermission('orders_edit') && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-order-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Order
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Order</DialogTitle>
                <DialogDescription>
                  Create a new order for a customer's vehicle purchase.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3 pb-4">
                  {/* Customer Selection */}
                <div>
                  <Label htmlFor="customer_id" className="text-sm font-medium">Customer</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <SearchableSelect
                        value={formData.customer_id}
                        onValueChange={(value) => {
                          setFormData({...formData, customer_id: value});
                          if (value) {
                            axios.get(`${API}/wallets/${value}`).then(res => setCustomerWallet(res.data)).catch(() => setCustomerWallet(null));
                          } else {
                            setCustomerWallet(null);
                          }
                        }}
                        options={customers.map(customer => ({
                          value: customer.id,
                          label: `${customer.full_name} (${customer.email})`
                        }))}
                        placeholder="Select customer"
                        searchPlaceholder="Search customers..."
                        emptyText="No customer found."
                      />
                    </div>
                    {formData.customer_id && (
                      <Badge variant={customerCurrency === 'YEN' ? 'default' : 'secondary'} className="whitespace-nowrap">
                        {customerCurrency}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Wallet Balance & Use Wallet */}
                {customerWallet && (customerWallet.balance_usd > 0 || customerWallet.balance_jpy > 0) && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-emerald-900">
                        <DollarSign className="h-4 w-4 inline mr-1" />
                        Customer Wallet Balance
                      </span>
                      <span className="text-sm font-bold text-emerald-700">
                        ${(customerWallet.balance_usd || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                        {customerWallet.balance_jpy > 0 && ` / ¥${customerWallet.balance_jpy.toLocaleString()}`}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="use_wallet"
                        checked={useWallet}
                        onCheckedChange={setUseWallet}
                      />
                      <Label htmlFor="use_wallet" className="text-sm text-emerald-800 cursor-pointer">
                        Deduct from wallet when order is placed
                      </Label>
                    </div>
                  </div>
                )}

                {/* Vehicle Selection */}
                <div>
                  <Label htmlFor="vehicle_ids" className="text-sm font-medium">Vehicle(s) - Select One or Multiple</Label>
                  
                  {/* Selected Vehicles Display */}
                  {formData.vehicle_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md mb-2">
                      {formData.vehicle_ids.map(vehicleId => {
                        const vehicle = Array.isArray(vehicles) ? vehicles.find(v => v.id === vehicleId) : null;
                        if (!vehicle) return null;
                        return (
                          <Badge key={vehicleId} variant="secondary" className="flex items-center gap-1 px-2 py-1">
                            <Car className="h-3 w-3" />
                            <span className="text-xs">{vehicle.year} {vehicle.model} - {vehicle.stock_no}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveVehicle(vehicleId)}
                              className="ml-1 hover:text-red-600"
                            >
                              ×
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  
                    {/* Searchable Dropdown */}
                  <SearchableSelect
                    value=""
                    onValueChange={handleVehicleSelect}
                    options={(Array.isArray(vehicles) ? vehicles : [])
                      .filter(v => !formData.vehicle_ids.includes(v.id) && !vehiclesInOrders.has(v.id))
                      .map(vehicle => ({
                          value: vehicle.id,
                          label: `${vehicle.year} ${vehicle.model} - ${vehicle.stock_no} (${vehicle.status})`
                        }))}
                    placeholder="Search and select vehicles..."
                    searchPlaceholder="Search vehicles..."
                    emptyText="No more vehicles available."
                  />
                  
                  {vehiclesInOrders.size > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Vehicles already assigned to other orders are hidden from the list.
                    </p>
                  )}
                </div>

                {/* Per-Vehicle Selling Prices (for multi-vehicle orders) */}
                {formData.vehicle_ids.length > 1 && (
                  <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="text-sm font-medium text-amber-900">Per-Vehicle Selling Prices (Optional)</Label>
                    <p className="text-xs text-amber-700">Set individual selling prices for each vehicle. Leave empty to split equally.</p>
                    {formData.vehicle_ids.map((vehicleId) => {
                      const vehicle = Array.isArray(vehicles) ? vehicles.find(v => v.id === vehicleId) : null;
                      if (!vehicle) return null;
                      return (
                        <div key={vehicleId} className="flex items-center gap-3">
                          <span className="text-xs text-slate-600 w-48 truncate">
                            {vehicle.stock_no} — {vehicle.year} {vehicle.model}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">¥</span>
                            <Input
                              type="number"
                              value={vehiclePrices[vehicleId] || ''}
                              onChange={(e) => setVehiclePrices({...vehiclePrices, [vehicleId]: e.target.value})}
                              placeholder="Auto-split"
                              className="h-8 w-32 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Order Status */}
                <div>
                  <Label htmlFor="status" className="text-sm font-medium">Order Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="reserved">Reserved</SelectItem>
                      <SelectItem value="purchased">Purchased</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="booking_applied">Booking Applied</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="on_the_way">On The Way</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Order Date (admin only) */}
                {user?.role === 'admin' && (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="order_date" className="text-sm font-medium">Order Date</Label>
                      <Input
                        id="order_date"
                        type="date"
                        value={formData.order_date}
                        onChange={(e) => setFormData((prev) => ({
                          ...prev,
                          order_date: e.target.value,
                          reservation_valid_date: e.target.value ? addDays(e.target.value, 3) : prev.reservation_valid_date
                        }))}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reservation_valid_date" className="text-sm font-medium">Reservation Valid Date</Label>
                      <Input
                        id="reservation_valid_date"
                        type="date"
                        value={formData.reservation_valid_date}
                        onChange={(e) => setFormData({...formData, reservation_valid_date: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Per-Vehicle Cost Breakdown Section */}
                {formData.vehicle_ids.length > 0 && (
                  <div className="space-y-3">
                    {formData.vehicle_ids.map((vehicleId, index) => {
                      const vehicle = Array.isArray(vehicles) ? vehicles.find(v => v.id === vehicleId) : null;
                      if (!vehicle) return null;
                      
                      return (
                        <div key={vehicleId}>
                          {/* Vehicle Name Header */}
                          <h4 className="font-medium text-sm text-slate-900 mb-2">
                            "{vehicle.year} {vehicle.brand || vehicle.make || ''} {vehicle.model} - {vehicle.stock_no}"
                          </h4>
                          
                          {/* Cost Breakdown for this vehicle */}
                          <div className="space-y-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <h3 className="font-semibold text-sm text-red-900 flex items-center">
                              <DollarSign className="h-4 w-4 mr-1" />
                              Cost Breakdown ({customerCurrency})
                            </h3>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label htmlFor={`fob_${vehicleId}`} className="text-xs text-red-700">FOB ({costCurrency})</Label>
                                <Input
                                  id={`fob_${vehicleId}`}
                                  type="number"
                                  step="0.01"
                                  value={displayUsd(vehicleCosts[vehicleId]?.fob_usd)}
                                  onChange={(e) => handleVehicleCostChange(vehicleId, 'fob_usd', e.target.value)}
                                  placeholder="2500"
                                  className="h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>

                              <div>
                                <Label htmlFor={`freight_${vehicleId}`} className="text-xs text-red-700">Freight ({costCurrency})</Label>
                                <Input
                                  id={`freight_${vehicleId}`}
                                  type="number"
                                  step="0.01"
                                  value={displayUsd(vehicleCosts[vehicleId]?.freight_usd)}
                                  onChange={(e) => handleVehicleCostChange(vehicleId, 'freight_usd', e.target.value)}
                                  placeholder="3500"
                                  className="h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>

                              <div>
                                <Label htmlFor={`vanning_${vehicleId}`} className="text-xs text-red-700">Vanning ({costCurrency})</Label>
                                <Input
                                  id={`vanning_${vehicleId}`}
                                  type="number"
                                  step="0.01"
                                  value={displayUsd(vehicleCosts[vehicleId]?.vanning_usd)}
                                  onChange={(e) => handleVehicleCostChange(vehicleId, 'vanning_usd', e.target.value)}
                                  placeholder="300"
                                  className="h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>

                              <div>
                                <Label htmlFor={`inspection_${vehicleId}`} className="text-xs text-red-700">Inspection ({costCurrency})</Label>
                                <Input
                                  id={`inspection_${vehicleId}`}
                                  type="number"
                                  step="0.01"
                                  value={displayUsd(vehicleCosts[vehicleId]?.inspection_usd)}
                                  onChange={(e) => handleVehicleCostChange(vehicleId, 'inspection_usd', e.target.value)}
                                  placeholder="300"
                                  className="h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>
                            </div>
                            {/* Reverse Mileage - Per Vehicle */}
                            <div className="mt-2 pt-2 border-t border-red-100">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id={`reverse_mileage_${vehicleId}`}
                                  checked={reverseMileagePerVehicle[vehicleId]?.requested || false}
                                  onCheckedChange={(checked) => {
                                    setReverseMileagePerVehicle(prev => ({
                                      ...prev,
                                      [vehicleId]: { requested: checked, display_mileage: checked ? (prev[vehicleId]?.display_mileage || '') : '' }
                                    }));
                                  }}
                                />
                                <Label htmlFor={`reverse_mileage_${vehicleId}`} className="text-xs font-medium text-amber-900 cursor-pointer">
                                  Apply Reverse Mileage ($100)
                                </Label>
                              </div>
                              {reverseMileagePerVehicle[vehicleId]?.requested && (
                                <div className="ml-6 mt-1">
                                  <Label htmlFor={`display_mileage_${vehicleId}`} className="text-xs text-amber-800">Display Mileage (km)</Label>
                                  <Input
                                    id={`display_mileage_${vehicleId}`}
                                    type="number"
                                    value={reverseMileagePerVehicle[vehicleId]?.display_mileage || ''}
                                    onChange={(e) => setReverseMileagePerVehicle(prev => ({
                                      ...prev,
                                      [vehicleId]: { ...prev[vehicleId], requested: true, display_mileage: e.target.value }
                                    }))}
                                    placeholder="Enter mileage shown to customer"
                                    className="h-8 text-sm max-w-xs mt-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-xs text-amber-700 text-right">
                      Reverse mileage fee: ${(formData.vehicle_ids.filter(id => reverseMileagePerVehicle[id]?.requested).length * 100).toLocaleString()} USD
                    </p>
                  </div>
                )}

                {/* Exchange Rate */}
                <div>
                  <Label htmlFor="exchange_rate" className="text-sm font-medium">Exchange Rate (JPY/USD)</Label>
                  <Input
                    id="exchange_rate"
                    name="exchange_rate"
                    type="number"
                    step="0.01"
                    value={formData.exchange_rate}
                    onChange={handleInputChange}
                    placeholder="150"
                    className="h-9"
                  />
                </div>

                {/* Total Amounts */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="total_amount_jpy" className="text-sm font-medium">Total Amount (JPY)</Label>
                    <Input
                      id="total_amount_jpy"
                      name="total_amount_jpy"
                      type="number"
                      value={formData.total_amount_jpy}
                      readOnly
                      className="h-9 bg-slate-50 cursor-not-allowed"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="total_amount_usd" className="text-sm font-medium">Total Amount (USD)</Label>
                    <Input
                      id="total_amount_usd"
                      name="total_amount_usd"
                      type="number"
                      step="0.01"
                      value={formData.total_amount_usd}
                      readOnly
                      className="h-9 bg-slate-50 cursor-not-allowed"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Profit Calculator - Collapsible for Multiple Vehicles */}
                {formData.vehicle_ids.length > 0 && (
                  <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-2">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Calculator className="h-5 w-5 text-blue-600" />
                        Profit Calculator {formData.vehicle_ids.length > 1 && `(${formData.vehicle_ids.length} Vehicles)`}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Individual Vehicle Cost Breakdowns - Collapsible */}
                      {formData.vehicle_ids.map((vehicleId, index) => {
                        const vehicle = Array.isArray(vehicles) ? vehicles.find(v => v.id === vehicleId) : null;
                        if (!vehicle) return null;
                        const vehicleCost = calculateVehicleTotalCost(vehicle);
                        
                        return (
                          <Collapsible key={vehicleId} defaultOpen={formData.vehicle_ids.length === 1}>
                            <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-white rounded-lg border hover:bg-slate-50">
                              <div className="flex items-center gap-2">
                                <Car className="h-4 w-4 text-blue-600" />
                                <span className="font-semibold text-sm">
                                  {formData.vehicle_ids.length > 1 && `Vehicle ${index + 1}: `}
                                  {vehicle.year} {vehicle.model} - {vehicle.stock_no}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-slate-900">
                                  ¥{vehicleCost.toLocaleString()}
                                </span>
                                <ChevronDown className="h-4 w-4 text-slate-500" />
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="pt-2 pb-2 px-3 bg-white rounded-b-lg border-x border-b">
                              <div className="space-y-2">
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                  <div>
                                    <p className="text-slate-600 text-xs mb-1">Vehicle Cost (from Cost Calculator)</p>
                                    <p className="font-bold text-slate-900">¥{vehicleCost.toLocaleString()}</p>
                                    <p className="text-xs text-slate-500">${(vehicleCost / getVehicleExchangeRate(vehicle)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                                    <p className="text-xs text-blue-600">Rate: {getVehicleExchangeRate(vehicle)} JPY/USD</p>
                                  </div>
                                </div>
                                <div className="pt-2 border-t text-xs text-slate-600">
                                  <p className="font-medium text-slate-700">Cost Breakdown (matches Vehicle Cost Calculator):</p>
                                  <p className="mt-1">
                                    <strong>Purchase:</strong> ¥{(vehicle.purchase_cost_jpy || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>Inland:</strong> Auction ¥{(vehicle.inland_auction || 0).toLocaleString()} + 
                                    Transport ¥{(vehicle.inland_transport || 0).toLocaleString()} + 
                                    Inspection ¥{(vehicle.inland_inspection || 0).toLocaleString()} + 
                                    Repair ¥{(vehicle.inspection_repair_charges || 0).toLocaleString()} + 
                                    Treatment ¥{(vehicle.inland_treatment || 0).toLocaleString()} + 
                                    Mileage ¥{(vehicle.inland_mileage_reversal || 0).toLocaleString()} + 
                                    Export Cert ¥{(vehicle.inland_export_certificate || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>+ Domestic Shipping:</strong> Shipping ¥{(vehicle.domestic_shipping_shipping || 0).toLocaleString()} + 
                                    Radiation ¥{(vehicle.domestic_shipping_radiation || 0).toLocaleString()} + 
                                    Photos ¥{(vehicle.domestic_shipping_photos || 0).toLocaleString()} + 
                                    Engine # ¥{(vehicle.domestic_shipping_engine_number || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>+ Vanning:</strong> ¥{(vehicle.vanning_cost_jpy || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>+ Freight:</strong> {(vehicle.freight_m3_unit || 0).toFixed(2)}m³ × ¥{(vehicle.freight_m3_rate || 0).toLocaleString()} × {(vehicle.freight_exchange_rate || 148.0827).toFixed(4)} = ¥{(vehicle.freight_per_car_jpy || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>+ Others:</strong> ¥{(vehicle.others_total || 0).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                      
                      {/* Total Summary - Always Visible */}
                      <div className="pt-3 border-t-2 border-slate-300">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-slate-600 mb-1">CFR Cost (JPY)</p>
                            <p className="font-bold text-slate-900 text-lg">¥{calculateSelectedVehiclesCosts().totalVehicleCostJpy.toLocaleString()}</p>
                            <p className="text-xs text-slate-500">${calculateSelectedVehiclesCosts().totalVehicleCostUsd.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                          </div>
                          <div>
                            <p className="text-slate-600 mb-1">CFR (Sell Price)</p>
                            <p className="font-bold text-slate-900 text-lg">¥{(parseFloat(formData.total_amount_jpy) || 0).toLocaleString()}</p>
                            <p className="text-xs text-slate-500">${(parseFloat(formData.total_amount_usd) || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                          </div>
                          <div>
                            <p className="text-slate-600 mb-1 flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              Estimate Profit
                            </p>
                            <p className={`font-bold text-xl ${calculateProfit().profitJPY >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ¥{calculateProfit().profitJPY.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                            </p>
                            <p className={`text-sm ${calculateProfit().profitUSD >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ${calculateProfit().profitUSD.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Consignee Information Section */}
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-sm text-blue-900 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    CONSIGNEE INFORMATION
                  </h3>
                  
                  <div>
                    <Label htmlFor="consignee_name" className="uppercase">CONSIGNEE NAME:</Label>
                    <Input
                      id="consignee_name"
                      name="consignee_name"
                      value={formData.consignee_name}
                      onChange={handleInputChange}
                      placeholder="ENTER CONSIGNEE NAME"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>

                  <div>
                    <Label htmlFor="consignee_address" className="uppercase">CONSIGNEE ADDRESS:</Label>
                    <Textarea
                      id="consignee_address"
                      name="consignee_address"
                      value={formData.consignee_address}
                      onChange={handleInputChange}
                      placeholder="ENTER CONSIGNEE ADDRESS"
                      className="uppercase placeholder:normal-case"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="consignee_phone" className="uppercase">CONSIGNEE PHONE:</Label>
                    <Input
                      id="consignee_phone"
                      name="consignee_phone"
                      value={formData.consignee_phone}
                      onChange={handleInputChange}
                      placeholder="+92 XXX XXXXXXX"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>

                  <div>
                    <Label htmlFor="consignee_email" className="uppercase">CONSIGNEE EMAIL ADDRESS:</Label>
                    <Input
                      id="consignee_email"
                      name="consignee_email"
                      type="email"
                      value={formData.consignee_email}
                      onChange={handleInputChange}
                      placeholder="consignee@example.com"
                    />
                  </div>
                </div>

                {/* Notifier Information Section */}
                <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm text-purple-900 flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      NOTIFIER INFORMATION
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sameAsConsignee"
                        checked={sameAsConsignee}
                        onCheckedChange={handleSameAsConsigneeChange}
                      />
                      <Label htmlFor="sameAsConsignee" className="uppercase cursor-pointer font-semibold">
                        SAME AS CONSIGNEE
                      </Label>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="notifier_name" className="uppercase">NOTIFIER NAME:</Label>
                    <Input
                      id="notifier_name"
                      name="notifier_name"
                      value={formData.notifier_name}
                      onChange={handleNotifierChange}
                      placeholder="ENTER NOTIFIER NAME"
                      className="uppercase placeholder:normal-case"
                      disabled={sameAsConsignee}
                    />
                  </div>

                  <div>
                    <Label htmlFor="notifier_address" className="uppercase">NOTIFIER ADDRESS:</Label>
                    <Textarea
                      id="notifier_address"
                      name="notifier_address"
                      value={formData.notifier_address}
                      onChange={handleNotifierChange}
                      placeholder="ENTER NOTIFIER ADDRESS"
                      className="uppercase placeholder:normal-case"
                      rows={2}
                      disabled={sameAsConsignee}
                    />
                  </div>

                  <div>
                    <Label htmlFor="notifier_phone" className="uppercase">NOTIFIER PHONE:</Label>
                    <Input
                      id="notifier_phone"
                      name="notifier_phone"
                      value={formData.notifier_phone}
                      onChange={handleNotifierChange}
                      placeholder="+92 XXX XXXXXXX"
                      className="uppercase placeholder:normal-case"
                      disabled={sameAsConsignee}
                    />
                  </div>

                  <div>
                    <Label htmlFor="notifier_email" className="uppercase">NOTIFIER EMAIL ADDRESS:</Label>
                    <Input
                      id="notifier_email"
                      name="notifier_email"
                      type="email"
                      value={formData.notifier_email}
                      onChange={handleNotifierChange}
                      placeholder="notifier@example.com"
                      disabled={sameAsConsignee}
                    />
                  </div>
                </div>

                {/* Additional Information Section */}
                <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-sm text-green-900 flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    ADDITIONAL INFORMATION
                  </h3>
                  
                  <div>
                    <Label htmlFor="company_registration_no" className="uppercase">COMPANY REGISTRATION #:</Label>
                    <Input
                      id="company_registration_no"
                      name="company_registration_no"
                      value={formData.company_registration_no}
                      onChange={handleInputChange}
                      placeholder="ENTER COMPANY REGISTRATION NUMBER"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>

                  <div>
                    <Label htmlFor="vat_no" className="uppercase">VAT #:</Label>
                    <Input
                      id="vat_no"
                      name="vat_no"
                      value={formData.vat_no}
                      onChange={handleInputChange}
                      placeholder="ENTER VAT NUMBER"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>

                  <div>
                    <Label htmlFor="passport_number" className="uppercase">PASSPORT NUMBER:</Label>
                    <Input
                      id="passport_number"
                      name="passport_number"
                      value={formData.passport_number}
                      onChange={handleInputChange}
                      placeholder="ENTER PASSPORT NUMBER"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Additional notes about the order..."
                    rows={3}
                  />
                </div>

                {/* Employee Assignment - Only for admin */}
                {user?.role === 'admin' && (
                  <div>
                    <Label htmlFor="assigned_employee_id" className="text-sm font-medium">Assign Employee (Agent)</Label>
                    <SearchableSelect
                      value={formData.assigned_employee_id}
                      onValueChange={(value) => setFormData({...formData, assigned_employee_id: value})}
                      options={[
                        { value: '', label: 'No employee assigned' },
                        ...employees.map(emp => ({
                          value: emp.id,
                          label: `${emp.full_name} (${emp.email})`
                        }))
                      ]}
                      placeholder="Select employee (optional)"
                      searchPlaceholder="Search employees..."
                      emptyText="No employee found."
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Employee will earn commission when this order is completed
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                    Create Order
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Order Dialog */}
        {user?.role !== 'customer' && (
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Order</DialogTitle>
                <DialogDescription>
                  Update order details and recalculate profit.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3 pb-4">
                {/* Customer Selection */}
                <div>
                  <Label htmlFor="edit_customer_id" className="text-sm font-medium">Customer</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <SearchableSelect
                        value={formData.customer_id}
                        onValueChange={(value) => setFormData({...formData, customer_id: value})}
                        options={customers.map(customer => ({
                          value: customer.id,
                          label: `${customer.full_name} (${customer.email})`
                        }))}
                        placeholder="Select customer"
                        searchPlaceholder="Search customers..."
                        emptyText="No customer found."
                      />
                    </div>
                    {formData.customer_id && (
                      <Badge variant={customerCurrency === 'YEN' ? 'default' : 'secondary'} className="whitespace-nowrap">
                        {customerCurrency}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Vehicle Selection */}
                <div>
                  <Label htmlFor="edit_vehicle_ids" className="text-sm font-medium">Vehicle(s) - Select One or Multiple</Label>
                  
                  {/* Selected Vehicles Display */}
                  {formData.vehicle_ids && formData.vehicle_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md mb-2">
                      {formData.vehicle_ids.map(vehicleId => {
                        const vehicle = Array.isArray(vehicles) ? vehicles.find(v => v.id === vehicleId) : null;
                        if (!vehicle) return null;
                        return (
                          <Badge key={vehicleId} variant="secondary" className="flex items-center gap-1 px-2 py-1">
                            <Car className="h-3 w-3" />
                            <span className="text-xs">{vehicle.year} {vehicle.model} - {vehicle.stock_no}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveVehicle(vehicleId)}
                              className="ml-1 hover:text-red-600"
                            >
                              ×
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  
                    {/* Searchable Dropdown */}
                  <SearchableSelect
                    value=""
                    onValueChange={handleVehicleSelect}
                    options={(Array.isArray(vehicles) ? vehicles : [])
                      .filter(v => !formData.vehicle_ids.includes(v.id) && !vehiclesInOrders.has(v.id))
                      .map(vehicle => ({
                          value: vehicle.id,
                          label: `${vehicle.year} ${vehicle.model} - ${vehicle.stock_no} (${vehicle.status})`
                        }))}
                    placeholder="Search and select vehicles..."
                    searchPlaceholder="Search vehicles..."
                    emptyText="No more vehicles available."
                  />
                  
                  {vehiclesInOrders.size > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Vehicles already assigned to other orders are hidden from the list.
                    </p>
                  )}
                </div>

                {/* Order Status */}
                <div>
                  <Label htmlFor="edit_status" className="text-sm font-medium">Order Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="reserved">Reserved</SelectItem>
                      <SelectItem value="purchased">Purchased</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="booking_applied">Booking Applied</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="on_the_way">On The Way</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Order Date (admin only) */}
                {user?.role === 'admin' && (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="edit_order_date" className="text-sm font-medium">Order Date</Label>
                      <Input
                        id="edit_order_date"
                        type="date"
                        value={formData.order_date}
                        onChange={(e) => setFormData((prev) => ({
                          ...prev,
                          order_date: e.target.value,
                          reservation_valid_date: e.target.value ? addDays(e.target.value, 3) : prev.reservation_valid_date
                        }))}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit_reservation_valid_date" className="text-sm font-medium">Reservation Valid Date</Label>
                      <Input
                        id="edit_reservation_valid_date"
                        type="date"
                        value={formData.reservation_valid_date}
                        onChange={(e) => setFormData({...formData, reservation_valid_date: e.target.value})}
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Per-Vehicle Cost Breakdown Section */}
                {formData.vehicle_ids && formData.vehicle_ids.length > 0 && (
                  <div className="space-y-3">
                    {formData.vehicle_ids.map((vehicleId, index) => {
                      const vehicle = Array.isArray(vehicles) ? vehicles.find(v => v.id === vehicleId) : null;
                      if (!vehicle) return null;
                      
                      return (
                        <div key={vehicleId}>
                          {/* Vehicle Name Header */}
                          <h4 className="font-medium text-sm text-slate-900 mb-2">
                            "{vehicle.year} {vehicle.brand || vehicle.make || ''} {vehicle.model} - {vehicle.stock_no}"
                          </h4>
                          
                          {/* Cost Breakdown for this vehicle */}
                          <div className="space-y-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <h3 className="font-semibold text-sm text-red-900 flex items-center">
                              <DollarSign className="h-4 w-4 mr-1" />
                              Cost Breakdown ({customerCurrency})
                            </h3>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label htmlFor={`edit_fob_${vehicleId}`} className="text-xs text-red-700">FOB ({costCurrency})</Label>
                                <Input
                                  id={`edit_fob_${vehicleId}`}
                                  type="number"
                                  step="0.01"
                                  value={displayUsd(vehicleCosts[vehicleId]?.fob_usd)}
                                  onChange={(e) => handleVehicleCostChange(vehicleId, 'fob_usd', e.target.value)}
                                  placeholder="2500"
                                  className="h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>

                              <div>
                                <Label htmlFor={`edit_freight_${vehicleId}`} className="text-xs text-red-700">Freight ({costCurrency})</Label>
                                <Input
                                  id={`edit_freight_${vehicleId}`}
                                  type="number"
                                  step="0.01"
                                  value={displayUsd(vehicleCosts[vehicleId]?.freight_usd)}
                                  onChange={(e) => handleVehicleCostChange(vehicleId, 'freight_usd', e.target.value)}
                                  placeholder="3500"
                                  className="h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>

                              <div>
                                <Label htmlFor={`edit_vanning_${vehicleId}`} className="text-xs text-red-700">Vanning ({costCurrency})</Label>
                                <Input
                                  id={`edit_vanning_${vehicleId}`}
                                  type="number"
                                  step="0.01"
                                  value={displayUsd(vehicleCosts[vehicleId]?.vanning_usd)}
                                  onChange={(e) => handleVehicleCostChange(vehicleId, 'vanning_usd', e.target.value)}
                                  placeholder="300"
                                  className="h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>

                              <div>
                                <Label htmlFor={`edit_inspection_${vehicleId}`} className="text-xs text-red-700">Inspection ({costCurrency})</Label>
                                <Input
                                  id={`edit_inspection_${vehicleId}`}
                                  type="number"
                                  step="0.01"
                                  value={displayUsd(vehicleCosts[vehicleId]?.inspection_usd)}
                                  onChange={(e) => handleVehicleCostChange(vehicleId, 'inspection_usd', e.target.value)}
                                  placeholder="300"
                                  className="h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>
                            </div>
                            {/* Reverse Mileage - Per Vehicle (Edit) */}
                            <div className="mt-2 pt-2 border-t border-red-100">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id={`edit_reverse_mileage_${vehicleId}`}
                                  checked={reverseMileagePerVehicle[vehicleId]?.requested || false}
                                  onCheckedChange={(checked) => {
                                    setReverseMileagePerVehicle(prev => ({
                                      ...prev,
                                      [vehicleId]: { requested: checked, display_mileage: checked ? (prev[vehicleId]?.display_mileage || '') : '' }
                                    }));
                                  }}
                                />
                                <Label htmlFor={`edit_reverse_mileage_${vehicleId}`} className="text-xs font-medium text-amber-900 cursor-pointer">
                                  Apply Reverse Mileage ($100)
                                </Label>
                              </div>
                              {reverseMileagePerVehicle[vehicleId]?.requested && (
                                <div className="ml-6 mt-1">
                                  <Label htmlFor={`edit_display_mileage_${vehicleId}`} className="text-xs text-amber-800">Display Mileage (km)</Label>
                                  <Input
                                    id={`edit_display_mileage_${vehicleId}`}
                                    type="number"
                                    value={reverseMileagePerVehicle[vehicleId]?.display_mileage || ''}
                                    onChange={(e) => setReverseMileagePerVehicle(prev => ({
                                      ...prev,
                                      [vehicleId]: { ...prev[vehicleId], requested: true, display_mileage: e.target.value }
                                    }))}
                                    placeholder="Enter mileage shown to customer"
                                    className="h-8 text-sm max-w-xs mt-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-xs text-amber-700 text-right">
                      Reverse mileage fee: ${(formData.vehicle_ids.filter(id => reverseMileagePerVehicle[id]?.requested).length * 100).toLocaleString()} USD
                    </p>
                  </div>
                )}

                {/* Exchange Rate */}
                <div>
                  <Label htmlFor="edit_exchange_rate" className="text-sm font-medium">Exchange Rate (JPY/USD)</Label>
                  <Input
                    id="edit_exchange_rate"
                    name="exchange_rate"
                    type="number"
                    step="0.01"
                    value={formData.exchange_rate}
                    onChange={handleInputChange}
                    placeholder="150"
                    className="h-9"
                  />
                </div>

                {/* Total Amounts */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit_total_amount_jpy" className="text-sm font-medium">Total Amount (JPY)</Label>
                    <Input
                      id="edit_total_amount_jpy"
                      name="total_amount_jpy"
                      type="number"
                      value={formData.total_amount_jpy}
                      readOnly
                      className="h-9 bg-slate-50 cursor-not-allowed"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_total_amount_usd" className="text-sm font-medium">Total Amount (USD)</Label>
                    <Input
                      id="edit_total_amount_usd"
                      name="total_amount_usd"
                      type="number"
                      step="0.01"
                      value={formData.total_amount_usd}
                      readOnly
                      className="h-9 bg-slate-50 cursor-not-allowed"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Profit Calculator in Edit Dialog - Multi-Vehicle Support */}
                {formData.vehicle_ids && formData.vehicle_ids.length > 0 && (
                  <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-2">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Calculator className="h-5 w-5 text-blue-600" />
                        Profit Calculator {formData.vehicle_ids.length > 1 && `(${formData.vehicle_ids.length} Vehicles)`}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Individual Vehicle Cost Breakdowns - Collapsible */}
                      {formData.vehicle_ids.map((vehicleId, index) => {
                        const vehicle = Array.isArray(vehicles) ? vehicles.find(v => v.id === vehicleId) : null;
                        if (!vehicle) return null;
                        const vehicleCost = calculateVehicleTotalCost(vehicle);
                        
                        return (
                          <Collapsible key={vehicleId} defaultOpen={formData.vehicle_ids.length === 1}>
                            <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-white rounded-lg border hover:bg-slate-50">
                              <div className="flex items-center gap-2">
                                <Car className="h-4 w-4 text-blue-600" />
                                <span className="font-semibold text-sm">
                                  {formData.vehicle_ids.length > 1 && `Vehicle ${index + 1}: `}
                                  {vehicle.year} {vehicle.model} - {vehicle.stock_no}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-slate-900">
                                  ¥{vehicleCost.toLocaleString()}
                                </span>
                                <ChevronDown className="h-4 w-4 text-slate-500" />
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="pt-2 pb-2 px-3 bg-white rounded-b-lg border-x border-b">
                              <div className="space-y-2">
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                  <div>
                                    <p className="text-slate-600 text-xs mb-1">Vehicle Cost (from Cost Calculator)</p>
                                    <p className="font-bold text-slate-900">¥{vehicleCost.toLocaleString()}</p>
                                    <p className="text-xs text-slate-500">${(vehicleCost / getVehicleExchangeRate(vehicle)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                                    <p className="text-xs text-blue-600">Rate: {getVehicleExchangeRate(vehicle)} JPY/USD</p>
                                  </div>
                                </div>
                                <div className="pt-2 border-t text-xs text-slate-600">
                                  <p className="font-medium text-slate-700">Cost Breakdown (matches Vehicle Cost Calculator):</p>
                                  <p className="mt-1">
                                    <strong>Purchase:</strong> ¥{(vehicle.purchase_cost_jpy || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>Inland:</strong> Auction ¥{(vehicle.inland_auction || 0).toLocaleString()} + 
                                    Transport ¥{(vehicle.inland_transport || 0).toLocaleString()} + 
                                    Inspection ¥{(vehicle.inland_inspection || 0).toLocaleString()} + 
                                    Repair ¥{(vehicle.inspection_repair_charges || 0).toLocaleString()} + 
                                    Treatment ¥{(vehicle.inland_treatment || 0).toLocaleString()} + 
                                    Mileage ¥{(vehicle.inland_mileage_reversal || 0).toLocaleString()} + 
                                    Export Cert ¥{(vehicle.inland_export_certificate || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>+ Domestic Shipping:</strong> Shipping ¥{(vehicle.domestic_shipping_shipping || 0).toLocaleString()} + 
                                    Radiation ¥{(vehicle.domestic_shipping_radiation || 0).toLocaleString()} + 
                                    Photos ¥{(vehicle.domestic_shipping_photos || 0).toLocaleString()} + 
                                    Engine # ¥{(vehicle.domestic_shipping_engine_number || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>+ Vanning:</strong> ¥{(vehicle.vanning_cost_jpy || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>+ Freight:</strong> {(vehicle.freight_m3_unit || 0).toFixed(2)}m³ × ¥{(vehicle.freight_m3_rate || 0).toLocaleString()} × {(vehicle.freight_exchange_rate || 148.0827).toFixed(4)} = ¥{(vehicle.freight_per_car_jpy || 0).toLocaleString()}
                                  </p>
                                  <p className="ml-4 mt-0.5">
                                    <strong>+ Others:</strong> ¥{(vehicle.others_total || 0).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                      
                      {/* Total Summary - Always Visible */}
                      <div className="pt-3 border-t-2 border-slate-300">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-slate-600 mb-1">CFR Cost (JPY)</p>
                            <p className="font-bold text-slate-900 text-lg">¥{calculateSelectedVehiclesCosts().totalVehicleCostJpy.toLocaleString()}</p>
                            <p className="text-xs text-slate-500">${calculateSelectedVehiclesCosts().totalVehicleCostUsd.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                          </div>
                          <div>
                            <p className="text-slate-600 mb-1">CFR (Sell Price)</p>
                            <p className="font-bold text-slate-900 text-lg">¥{(parseFloat(formData.total_amount_jpy) || 0).toLocaleString()}</p>
                            <p className="text-xs text-slate-500">${(parseFloat(formData.total_amount_usd) || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                          </div>
                          <div>
                            <p className="text-slate-600 mb-1 flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              Estimate Profit
                            </p>
                            <p className={`font-bold text-xl ${calculateProfit().profitJPY >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ¥{calculateProfit().profitJPY.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                            </p>
                            <p className={`text-sm ${calculateProfit().profitUSD >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ${calculateProfit().profitUSD.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Consignee Information Section */}
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-sm text-blue-900 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    CONSIGNEE INFORMATION
                  </h3>
                  
                  <div>
                    <Label htmlFor="edit_consignee_name" className="uppercase">CONSIGNEE NAME:</Label>
                    <Input
                      id="edit_consignee_name"
                      name="consignee_name"
                      value={formData.consignee_name}
                      onChange={handleInputChange}
                      placeholder="ENTER CONSIGNEE NAME"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit_consignee_address" className="uppercase">CONSIGNEE ADDRESS:</Label>
                    <Textarea
                      id="edit_consignee_address"
                      name="consignee_address"
                      value={formData.consignee_address}
                      onChange={handleInputChange}
                      placeholder="ENTER CONSIGNEE ADDRESS"
                      className="uppercase placeholder:normal-case"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit_consignee_phone" className="uppercase">CONSIGNEE PHONE:</Label>
                    <Input
                      id="edit_consignee_phone"
                      name="consignee_phone"
                      value={formData.consignee_phone}
                      onChange={handleInputChange}
                      placeholder="+92 XXX XXXXXXX"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit_consignee_email" className="uppercase">CONSIGNEE EMAIL ADDRESS:</Label>
                    <Input
                      id="edit_consignee_email"
                      name="consignee_email"
                      type="email"
                      value={formData.consignee_email}
                      onChange={handleInputChange}
                      placeholder="consignee@example.com"
                    />
                  </div>
                </div>

                {/* Notifier Information Section */}
                <div className="space-y-4 p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm text-purple-900 flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      NOTIFIER INFORMATION
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="edit_sameAsConsignee"
                        checked={sameAsConsignee}
                        onCheckedChange={handleSameAsConsigneeChange}
                      />
                      <Label htmlFor="edit_sameAsConsignee" className="uppercase cursor-pointer font-semibold">
                        SAME AS CONSIGNEE
                      </Label>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="edit_notifier_name" className="uppercase">NOTIFIER NAME:</Label>
                    <Input
                      id="edit_notifier_name"
                      name="notifier_name"
                      value={formData.notifier_name}
                      onChange={handleNotifierChange}
                      placeholder="ENTER NOTIFIER NAME"
                      className="uppercase placeholder:normal-case"
                      disabled={sameAsConsignee}
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit_notifier_address" className="uppercase">NOTIFIER ADDRESS:</Label>
                    <Textarea
                      id="edit_notifier_address"
                      name="notifier_address"
                      value={formData.notifier_address}
                      onChange={handleNotifierChange}
                      placeholder="ENTER NOTIFIER ADDRESS"
                      className="uppercase placeholder:normal-case"
                      rows={2}
                      disabled={sameAsConsignee}
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit_notifier_phone" className="uppercase">NOTIFIER PHONE:</Label>
                    <Input
                      id="edit_notifier_phone"
                      name="notifier_phone"
                      value={formData.notifier_phone}
                      onChange={handleNotifierChange}
                      placeholder="+92 XXX XXXXXXX"
                      className="uppercase placeholder:normal-case"
                      disabled={sameAsConsignee}
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit_notifier_email" className="uppercase">NOTIFIER EMAIL ADDRESS:</Label>
                    <Input
                      id="edit_notifier_email"
                      name="notifier_email"
                      type="email"
                      value={formData.notifier_email}
                      onChange={handleNotifierChange}
                      placeholder="notifier@example.com"
                      disabled={sameAsConsignee}
                    />
                  </div>
                </div>

                {/* Additional Information Section */}
                <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-sm text-green-900 flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    ADDITIONAL INFORMATION
                  </h3>
                  
                  <div>
                    <Label htmlFor="edit_company_registration_no" className="uppercase">COMPANY REGISTRATION #:</Label>
                    <Input
                      id="edit_company_registration_no"
                      name="company_registration_no"
                      value={formData.company_registration_no}
                      onChange={handleInputChange}
                      placeholder="ENTER COMPANY REGISTRATION NUMBER"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit_vat_no" className="uppercase">VAT #:</Label>
                    <Input
                      id="edit_vat_no"
                      name="vat_no"
                      value={formData.vat_no}
                      onChange={handleInputChange}
                      placeholder="ENTER VAT NUMBER"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit_passport_number" className="uppercase">PASSPORT NUMBER:</Label>
                    <Input
                      id="edit_passport_number"
                      name="passport_number"
                      value={formData.passport_number}
                      onChange={handleInputChange}
                      placeholder="ENTER PASSPORT NUMBER"
                      className="uppercase placeholder:normal-case"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit_notes">Notes (Optional)</Label>
                  <Textarea
                    id="edit_notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Additional notes about the order..."
                    rows={3}
                  />
                </div>

                {/* Employee Assignment - Only for admin */}
                {user?.role === 'admin' && (
                  <div>
                    <Label htmlFor="edit_assigned_employee_id" className="text-sm font-medium">Assign Employee (Agent)</Label>
                    <SearchableSelect
                      value={formData.assigned_employee_id}
                      onValueChange={(value) => setFormData({...formData, assigned_employee_id: value})}
                      options={[
                        { value: '', label: 'No employee assigned' },
                        ...employees.map(emp => ({
                          value: emp.id,
                          label: `${emp.full_name} (${emp.email})`
                        }))
                      ]}
                      placeholder="Select employee (optional)"
                      searchPlaceholder="Search employees..."
                      emptyText="No employee found."
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Employee will earn commission when this order is completed
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEditDialog(false);
                      setIsEditMode(false);
                      setSelectedOrder(null);
                      setSelectedVehicle(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Update Order
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by order ID, stock number, or chassis number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="reserved">Reserved</SelectItem>
                <SelectItem value="purchased">Purchased</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
                <SelectItem value="booking_applied">Booking Applied</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="on_the_way">On The Way</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Order List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {user?.role === 'customer' ? 'Your Orders' : 'All Orders'} ({filteredOrders.length})
              </CardTitle>
              <CardDescription>
                {user?.role === 'customer' 
                  ? 'Track the status of your vehicle orders'
                  : 'Manage customer orders and track progress'
                }
              </CardDescription>
            </div>
            {user?.role !== 'customer' && selectedOrders.length > 0 && (
              <div className="flex items-center space-x-2">
                <Select value={bulkStatusValue} onValueChange={handleBulkStatusChange}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={`Status (${selectedOrders.length})`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="reserved">Reserved</SelectItem>
                    <SelectItem value="purchased">Purchased</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="booking_applied">Booking Applied</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="on_the_way">On The Way</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedOrders.length})
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {user?.role !== 'customer' && (
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedOrders.length === orders.length && orders.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                    )}
                    <TableHead>Order ID</TableHead>
                    {user?.role !== 'customer' && <TableHead>Customer</TableHead>}
                    {user?.role !== 'customer' && <TableHead>Agent</TableHead>}
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-slate-50">
                      {user?.role !== 'customer' && (
                        <TableCell>
                          <Checkbox
                            checked={selectedOrders.includes(order.id)}
                            onCheckedChange={() => toggleSelectOrder(order.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm">
                        #{order.id.slice(-8).toUpperCase()}
                      </TableCell>
                      {user?.role !== 'customer' && (
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-slate-400" />
                            <span>{getCustomerInfo(order.customer_id)}</span>
                          </div>
                        </TableCell>
                      )}
                      {user?.role !== 'customer' && (
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-slate-400" />
                            <span>{order.assigned_employee?.full_name || 'Unassigned'}</span>
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Car className="h-4 w-4 text-slate-400" />
                          <span>{getVehicleCountDisplay(order)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">¥{order.total_amount_jpy?.toLocaleString()}</p>
                          <p className="text-sm text-slate-500">${order.total_amount_usd?.toLocaleString()}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {user?.role === 'customer' ? (
                          <Badge className={`${getStatusColor(order.status)} flex items-center space-x-1 w-fit`}>
                            {getStatusIcon(order.status)}
                            <span className="capitalize">{order.status}</span>
                          </Badge>
                        ) : (
                          <Select
                            value={order.status}
                            onValueChange={(newStatus) => updateOrderStatus(order.id, newStatus)}
                          >
                            <SelectTrigger className="w-36">
                              <div className="flex items-center space-x-2">
                                {getStatusIcon(order.status)}
                                <span className="capitalize">{order.status}</span>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="available">
                                <div className="flex items-center space-x-2">
                                  <Package className="h-4 w-4" />
                                  <span>Available</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="reserved">
                                <div className="flex items-center space-x-2">
                                  <Clock className="h-4 w-4" />
                                  <span>Reserved</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="purchased">
                                <div className="flex items-center space-x-2">
                                  <Package className="h-4 w-4" />
                                  <span>Purchased</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="sold">
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className="h-4 w-4" />
                                  <span>Sold</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="booking_applied">
                                <div className="flex items-center space-x-2">
                                  <Clock className="h-4 w-4" />
                                  <span>Booking Applied</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="shipped">
                                <div className="flex items-center space-x-2">
                                  <Clock className="h-4 w-4" />
                                  <span>Shipped</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="delivered">
                                <div className="flex items-center space-x-2">
                                  <CheckCircle className="h-4 w-4" />
                                  <span>Delivered</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="on_the_way">
                                <div className="flex items-center space-x-2">
                                  <Clock className="h-4 w-4" />
                                  <span>On The Way</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2 text-slate-500">
                          <Calendar className="h-4 w-4" />
                          <span className="text-sm">
                            {order.order_date ? new Date(order.order_date).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewOrderDetails(order.id)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                          {user?.role !== 'customer' && hasPermission('orders_edit') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditOrder(order)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </Button>
                          )}
                          
                          {/* Generate Invoice Dropdown - Admin/Employee Only */}
                          {user?.role !== 'customer' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  Generate
                                  <ChevronDown className="h-4 w-4 ml-2" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleGenerateInvoice(order.id, 'FINAL')}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  Invoice
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleGenerateInvoice(order.id, 'PROFORMA')}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  Proforma
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Orders Found</h3>
              <p className="text-slate-500 mb-6">
                {searchTerm || statusFilter 
                  ? 'No orders match your search criteria.' 
                  : user?.role === 'customer'
                    ? 'You have no orders yet. Contact our team to place your first order.'
                    : 'Start by creating your first customer order.'
                }
              </p>
              {user?.role !== 'customer' && !searchTerm && !statusFilter && (
                <Button 
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Order
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <DialogDescription>
              Complete information about this order
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer Information Section */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer Information
                </h4>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-2 text-sm">
                  {(() => {
                    // Use customer data from the order (already populated by backend)
                    const customer = selectedOrder.customer;
                    if (!customer && selectedOrder.customer_email) return <p className="text-slate-500">Customer: {selectedOrder.customer_email}</p>;
                    if (!customer) return <p className="text-slate-500">Customer information not available</p>;
                    return (
                      <>
                        <p><span className="font-medium text-slate-700">Name:</span> <span className="text-slate-900">{customer.full_name}</span></p>
                        <p><span className="font-medium text-slate-700">Email:</span> <span className="text-slate-900">{customer.email}</span></p>
                        <p><span className="font-medium text-slate-700">Phone:</span> <span className="text-slate-900">{customer.phone || customer.phone_number || 'Not provided'}</span></p>
                        {customer.company && (
                          <p><span className="font-medium text-slate-700">Company:</span> <span className="text-slate-900">{customer.company}</span></p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Consignee Information Section */}
              {(selectedOrder.consignee_name || selectedOrder.consignee_phone || selectedOrder.consignee_email) && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Consignee Information
                  </h4>
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4 space-y-2 text-sm">
                    {selectedOrder.consignee_name && (
                      <p><span className="font-medium text-slate-700">Full Name:</span> <span className="text-slate-900">{selectedOrder.consignee_name}</span></p>
                    )}
                    {selectedOrder.consignee_phone && (
                      <p><span className="font-medium text-slate-700">Phone:</span> <span className="text-slate-900">{selectedOrder.consignee_phone}</span></p>
                    )}
                    {selectedOrder.consignee_email && (
                      <p><span className="font-medium text-slate-700">Email:</span> <span className="text-slate-900">{selectedOrder.consignee_email}</span></p>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Order Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Order ID:</span> #{selectedOrder.id.slice(-8).toUpperCase()}</p>
                    <p><span className="font-medium">Date:</span> {selectedOrder.order_date ? new Date(selectedOrder.order_date).toLocaleDateString() : 'N/A'}</p>
                    <div><span className="font-medium">Status:</span> <Badge className={getStatusColor(selectedOrder.status)}>{selectedOrder.status}</Badge></div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Financial Details</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Amount (JPY):</span> ¥{selectedOrder.total_amount_jpy?.toLocaleString()}</p>
                    <p><span className="font-medium">Amount (USD):</span> ${selectedOrder.total_amount_usd?.toLocaleString()}</p>
                    <p><span className="font-medium">Exchange Rate:</span> ¥{selectedOrder.exchange_rate}/USD</p>
                  </div>
                </div>
              </div>
              
              {/* Vehicles Section - Card Style Layout */}
              {selectedOrder.vehicles && selectedOrder.vehicles.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Car className="h-4 w-4" />
                    {selectedOrder.vehicles.length > 1 ? `Vehicles (${selectedOrder.vehicles.length})` : 'Vehicle Details'}
                  </h4>
                  <div className="space-y-3">
                    {selectedOrder.vehicles.map((vehicle, index) => (
                      <Card key={vehicle.id} className="border-2 border-slate-200 hover:border-blue-300 transition-colors">
                        <CardContent className="p-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              {selectedOrder.vehicles.length > 1 && (
                                <Badge variant="outline" className="bg-blue-50">Vehicle {index + 1}</Badge>
                              )}
                              <h5 className="font-semibold text-lg text-slate-900">
                                {vehicle.year} {vehicle.brand || vehicle.make || ''} {vehicle.model}
                              </h5>
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                              <div>
                                <span className="font-medium text-slate-600">Stock No:</span>
                                <span className="ml-2 text-slate-900">{vehicle.stock_no || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="font-medium text-slate-600">Chassis No:</span>
                                <span className="ml-2 text-slate-900">{vehicle.chassis_no || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="font-medium text-slate-600">Color:</span>
                                <span className="ml-2 text-slate-900">{vehicle.color || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="font-medium text-slate-600">Mileage:</span>
                                <span className="ml-2 text-slate-900">{vehicle.mileage ? `${vehicle.mileage.toLocaleString()} km` : 'N/A'}</span>
                              </div>
                              {vehicle.engine_capacity && (
                                <div>
                                  <span className="font-medium text-slate-600">Engine:</span>
                                  <span className="ml-2 text-slate-900">{vehicle.engine_capacity}cc</span>
                                </div>
                              )}
                              {vehicle.transmission && (
                                <div>
                                  <span className="font-medium text-slate-600">Transmission:</span>
                                  <span className="ml-2 text-slate-900">{vehicle.transmission}</span>
                                </div>
                              )}
                            </div>
                            {/* Cost Breakdown (USD) - per-vehicle, falling back to legacy order-level costs */}
                            {(() => {
                              const vc = (selectedOrder.vehicle_costs && selectedOrder.vehicle_costs[vehicle.id]) || {};
                              const isOnlyVehicle = selectedOrder.vehicles.length === 1;
                              const fob = vc.fob_usd ?? (isOnlyVehicle ? selectedOrder.fob_usd : null);
                              const freight = vc.freight_usd ?? (isOnlyVehicle ? selectedOrder.freight_usd : null);
                              const vanning = vc.vanning_usd ?? (isOnlyVehicle ? selectedOrder.vanning_usd : null);
                              const inspection = vc.inspection_usd ?? (isOnlyVehicle ? selectedOrder.inspection_usd : null);
                              const total = (Number(fob) || 0) + (Number(freight) || 0) + (Number(vanning) || 0) + (Number(inspection) || 0);
                              if (total <= 0) return null;
                              const rows = [
                                ['FOB (USD)', fob],
                                ['Freight (USD)', freight],
                                ['Vanning (USD)', vanning],
                                ['Inspection (USD)', inspection]
                              ].filter(([, val]) => Number(val) > 0);
                              return (
                                <div className="pt-2 border-t border-slate-200">
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <DollarSign className="h-3.5 w-3.5 text-red-600" />
                                    <span className="text-xs font-semibold text-slate-700">Cost Breakdown (USD)</span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    {rows.map(([label, val]) => (
                                      <div key={label} className="flex justify-between">
                                        <span className="text-slate-600">{label}:</span>
                                        <span className="font-medium text-slate-900">{Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      </div>
                                    ))}
                                    <div className="flex justify-between col-span-2 pt-1 mt-1 border-t border-slate-100">
                                      <span className="font-medium text-slate-700">Total Cost:</span>
                                      <span className="font-semibold text-slate-900">{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                            {/* Reverse Mileage ($100 if applied) */}
                            {vehicle.reverse_mileage_requested && (
                              <div className="pt-2 border-t border-slate-200">
                                <div className="flex items-center flex-wrap gap-2">
                                  <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20">
                                    Apply Reverse Mileage ($100)
                                  </span>
                                  {vehicle.display_mileage != null && (
                                    <span className="text-xs text-slate-600">
                                      Display Mileage: {Number(vehicle.display_mileage).toLocaleString()} km
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {selectedOrder.notes && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Notes</h4>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send order status update email?</AlertDialogTitle>
            <AlertDialogDescription>
              {emailDialogBulkMode
                ? `Notify ${emailDialogOrderIds.length} customer(s) of their new status: ${emailDialogStatus.replace(/_/g, ' ')}?`
                : `Notify the customer of the new status: ${emailDialogStatus.replace(/_/g, ' ')}?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              executeStatusUpdate(emailDialogOrderIds, emailDialogStatus, false);
              if (emailDialogBulkMode) {
                setSelectedOrders([]);
                setBulkStatusValue('');
              }
            }}>
              Skip Email
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              executeStatusUpdate(emailDialogOrderIds, emailDialogStatus, true);
              if (emailDialogBulkMode) {
                setSelectedOrders([]);
                setBulkStatusValue('');
              }
            }}>
              Confirm & Send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrderManagement;